import { ADMIN_API_URL, ADMIN_APP_ID } from './apiConfig';

const AUTH_TOKEN_KEY = 'authToken';
const AUTH_USER_KEY = 'authUser';
const AUTH_LOGIN_SESSION_KEY = 'authLoginSession';
const CT_PROJECT_ID_KEY = 'ct_project_id';
const CT_PROJECT_NAME_KEY = 'ct_project_name';
const LOGIN_STORAGE_KEYS = [
  'access_token', 'refresh_token', 'id_token', 'token_type', 'expires_in',
  'refresh_expires_in', 'menu', 'footermenu', 'menuPermission', 'default_values',
  'roleId', 'companyName', 'preferred_Username', 'user_Email', 'userName',
  'userId', 'tenantId', 'businessId', 'businessUnitId', 'activeKey',
  'ct_project_id', 'ct_project_name', 'menus', 'permissions',
];

let loginPromise = null;

function extractToken(json) {
  return (
    json?.token ?? json?.access_token ?? json?.accessToken ?? json?.jwt ??
    json?.data?.token ?? json?.data?.access_token ?? json?.data?.accessToken ?? json?.data?.jwt
  );
}

function extractUser(json) {
  return json?.user ?? json?.data?.user ?? json?.data?.profile ?? null;
}

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')));
  } catch {
    return null;
  }
}

function setLocalStorageJson(key, value) {
  if (value === undefined || value === null) return;
  localStorage.setItem(key, JSON.stringify(value));
}

function getLoginMenus(json, data) {
  return data?.menus ?? json?.menus ?? {
    menu: data?.menu ?? json?.menu,
    footermenu: data?.footermenu ?? json?.footermenu,
  };
}

function persistLoginResponse(json, token, user) {
  const data = json?.data ?? json;
  const tokenPayload = decodeJwtPayload(token) ?? {};
  const menus = getLoginMenus(json, data);
  const userName = (
    user?.userName ?? user?.name ?? data?.userName ?? data?.name ??
    tokenPayload.userName ?? tokenPayload.name ?? tokenPayload.preferred_username ?? tokenPayload.email
  );
  const preferredUsername = (
    user?.preferred_Username ?? user?.preferred_username ??
    data?.preferred_Username ?? data?.preferred_username ??
    tokenPayload.preferred_Username ?? tokenPayload.preferred_username ??
    tokenPayload.email ?? userName
  );
  const userEmail = user?.email ?? data?.email ?? tokenPayload.email ?? preferredUsername;

  localStorage.setItem('access_token', token);
  if (data?.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
  if (data?.id_token !== undefined) localStorage.setItem('id_token', data.id_token);
  if (data?.token_type) localStorage.setItem('token_type', data.token_type);
  if (data?.expires_in !== undefined) localStorage.setItem('expires_in', String(data.expires_in));
  if (data?.refresh_expires_in !== undefined) localStorage.setItem('refresh_expires_in', String(data.refresh_expires_in));
  if (userName) localStorage.setItem('userName', userName);
  if (preferredUsername) localStorage.setItem('preferred_Username', preferredUsername);
  if (userEmail) localStorage.setItem('user_Email', userEmail);
  if (data?.roleId ?? tokenPayload.role_id) localStorage.setItem('roleId', data?.roleId ?? tokenPayload.role_id);
  if (data?.companyName) localStorage.setItem('companyName', data.companyName);
  if (tokenPayload.userId !== undefined) localStorage.setItem('userId', String(tokenPayload.userId));
  if (tokenPayload.tenantId) localStorage.setItem('tenantId', tokenPayload.tenantId);
  if (tokenPayload.businessId) localStorage.setItem('businessId', tokenPayload.businessId);
  if (tokenPayload.businessUnitId) localStorage.setItem('businessUnitId', tokenPayload.businessUnitId);

  setLocalStorageJson('menu', menus.menu);
  setLocalStorageJson('footermenu', menus.footermenu);
  setLocalStorageJson('menuPermission', data?.menuPermission);
  setLocalStorageJson('default_values', data?.default_values);
}

// ── Token & Session ──────────────────────────────────────────────────────────

export function getStoredToken() {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const hasSession = sessionStorage.getItem(AUTH_LOGIN_SESSION_KEY) === '1';
  return token && hasSession ? token : null;
}

export function getStoredUser() {
  const raw = localStorage.getItem(AUTH_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function logout() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  LOGIN_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  sessionStorage.removeItem(AUTH_LOGIN_SESSION_KEY);
  window.dispatchEvent(new Event('auth:logout'));
}

// ── Role Helpers ─────────────────────────────────────────────────────────────

export function getCurrentRoleName() {
  return String(localStorage.getItem('roleId') ?? '').trim().toLowerCase();
}

export function getUserRoles() {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const payload = decodeJwtPayload(token);
  const roles = new Set();
  const stored = getCurrentRoleName();
  if (stored) roles.add(stored);
  if (payload) {
    (payload?.realm_access?.roles ?? []).forEach((role) => roles.add(role));
    Object.values(payload?.resource_access ?? {}).forEach((client) => {
      (client?.roles ?? []).forEach((role) => roles.add(role));
    });
    [payload?.role, payload?.roleName, payload?.role_name].forEach((role) => {
      if (role) roles.add(role);
    });
  }
  return Array.from(roles).map((role) => String(role).toLowerCase());
}

export function isTester() {
  return getCurrentRoleName() === 'tester' || getUserRoles().includes('tester');
}

// ── Project Context ───────────────────────────────────────────────────────────

export function setSelectedProject(project) {
  if (!project) return;
  localStorage.setItem(CT_PROJECT_ID_KEY, String(project.id));
  localStorage.setItem(CT_PROJECT_NAME_KEY, String(project.name));
}

export function getSelectedProject() {
  const id = localStorage.getItem(CT_PROJECT_ID_KEY);
  const name = localStorage.getItem(CT_PROJECT_NAME_KEY);
  if (!id || !name) return null;
  return { id: Number(id), name };
}

export function clearSelectedProject() {
  localStorage.removeItem(CT_PROJECT_ID_KEY);
  localStorage.removeItem(CT_PROJECT_NAME_KEY);
}

// ── Login ────────────────────────────────────────────────────────────────────

export async function login(credentials) {
  if (loginPromise) return loginPromise;

  loginPromise = fetch(`${ADMIN_API_URL}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-App-Id': ADMIN_APP_ID,
    },
    body: JSON.stringify({
      email: credentials.email,
      user_pwd: credentials.password ?? credentials.user_pwd,
    }),
  })
    .then(async (res) => {
      const json = await res.json();
      const data = json?.data ?? json;
      if (!res.ok) {
        const error = new Error(json?.message ?? json?.error ?? `Login failed: ${res.status}`);
        error.status = res.status;
        throw error;
      }
      const token = extractToken(json);
      if (!token) throw new Error('No token in login response');
      const user = extractUser(json) ?? {
        userId: data.user_id,
        email: data.email,
        username: data.username,
        userTypeId: data.user_type_id,
        appId: data.app_id,
      };
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      sessionStorage.setItem(AUTH_LOGIN_SESSION_KEY, '1');
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
      localStorage.setItem('access_token', token);
      if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
      if (data.token_type) localStorage.setItem('token_type', data.token_type);
      if (data.user_id !== undefined) localStorage.setItem('userId', String(data.user_id));
      if (data.username) localStorage.setItem('userName', data.username);
      if (data.email) localStorage.setItem('user_Email', data.email);
      if (data.user_type_id !== undefined) localStorage.setItem('roleId', String(data.user_type_id));
      if (data.menus) localStorage.setItem('menus', JSON.stringify(data.menus));
      if (data.permissions) localStorage.setItem('permissions', JSON.stringify(data.permissions));
      persistLoginResponse(json, token, user);
      // Let any mounted AuthGuard re-check and reveal the protected content.
      window.dispatchEvent(new Event('auth:login'));
      return data;
    })
    .finally(() => { loginPromise = null; });

  return loginPromise;
}

// ── HTTP Helpers (exported for use by other service files) ───────────────────

export async function ensureToken() {
  const token = getStoredToken();
  if (token) return token;
  const error = new Error('Authentication required');
  error.status = 401;
  throw error;
}

async function authHeaders() {
  const token = await ensureToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchJsonWithAuth(baseUrl, path, options = {}) {
  const headers = await authHeaders();
  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: { ...headers, ...options.headers },
  });
  if (res.status === 401) logout();
  if (!res.ok) {
    const error = new Error(`API ${res.status}: ${res.statusText}`);
    error.status = res.status;
    throw error;
  }
  return res.json();
}

export async function apiGetWithAuth(baseUrl, path) {
  const json = await fetchJsonWithAuth(baseUrl, path);
  return json.data ?? json;
}
